import { useState, useRef, useCallback, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Button, Alert, Badge } from '@/components/ui';
import { TrashIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import { getImportanceColor } from '@/utils/colors';
import type { Point } from '@/types/canvas';
import AnnotationTimeline from './AnnotationTimeline';
import { type PredefinedRegion } from '@/services/annotationApi';
import HullVisualization, { type HullVisualizationHandle } from './HullVisualization';
import { findLandmarksInBounds, getLandmarkNames, LANDMARK_NAMES } from '@/utils/landmarkDetection';
import type { PoseData } from '@/utils/landmarkDetection';
import type {
  Phase,
  SpatialGoal,
  TimingConfig,
  AxisConfig,
  TemplateAnchor,
  JointAngleConfig,
  Notice,
  RegionResolution,
  GateSummary,
} from '@/types/api';
import { createAnchorFromClick } from '@/utils/barycentric';
import type { TrajectoryConfig } from '@/types/trajectory';
import PhaseTimeline from '@/components/phase/PhaseTimeline';
// Import the independent annotation panels
import BodyRegionAnnotation from './BodyRegionAnnotation';
import DistanceGoalAnnotation from './DistanceGoalAnnotation';
import MovementGoalAnnotation from './MovementGoalAnnotation';
import AxisLineAnnotation from './AxisLineAnnotation';
import TimingGoalAnnotation from './TimingGoalAnnotation';
import JointAngleGoalAnnotation from './JointAngleGoalAnnotation';
import InspectorSection from './InspectorSection';
import LandmarkPicker from './LandmarkPicker';
import { VideoPlaybackControls } from './VideoPlaybackControls';
import AnnotationCanvasOverlay from './AnnotationCanvasOverlay';
import GoalOverlayLayer from './GoalOverlayLayer';
import { getPoseDataWithRetry } from '@/services/videoApi';
import { saveAnnotation } from '@/services/annotationApi';
import type { SaveAnnotationPayload, SaveAnnotationResponse } from '@/services/annotationApi';
import { TIMEOUTS, FIELD_IDS } from '@/utils/constants';
import { useAdaptiveGuide } from '@/contexts/AdaptiveGuideContext';
import { getRegionPlacement } from '@/services/templateApi';
import {
  COUNTING_IN_SCORES_BADGE,
  NOT_COUNTING_YET_PENDING,
  NOT_COUNTING_YET_SHORT,
  PLACEMENT_BLOCKED_SENTENCES,
  PLACE_ON_VIDEO_BUSY_LABEL,
  PLACE_ON_VIDEO_LABEL,
  SELECTED_ON_CANVAS_HINT,
  focusAreaSideLine,
  regionWatchingSentence,
  type PlacementBlockedReason,
} from '@/utils/regionCardCopy';
import { gateSummarySentence } from '@/utils/gateCopy';

/**
 * Enhanced annotation types matching backend API
 */
export interface Annotation {
  id: string;
  type: 'rectangle' | 'circle' | 'line';
  /**
   * Where the box/circle/line sits on the video.
   *
   * OPTIONAL since PR-3 of the coach-input refactor: an annotation the backend
   * materialiser built from the coach's spoken instruction has no position yet
   * (owner decision D5 — canvas-less is canonical). The client used to invent
   * one, a box at (0.4, 0.4, 0.2 × 0.2) on every single suggestion, which
   * looked to the coach like a position someone had chosen and was not one.
   * The coach places it, or PR-4's assist derives a box from the body points
   * the check already resolved. Everything that DRAWS an annotation must skip
   * one with no coordinates; see `AnnotationCanvasOverlay`.
   */
  coordinates?: {
    x: number;
    y: number;
    width?: number;  // rectangle
    height?: number; // rectangle
    radius?: number; // circle
    x2?: number;     // line end point
    y2?: number;     // line end point
  };
  importance: 'low' | 'medium' | 'high' | 'critical';
  /**
   * Whether this region follows its body points, which is also what makes it
   * COUNT in the score.
   *
   * OPTIONAL since PR-3: a focus-area region the backend materialiser built
   * from the coach's instruction arrives with no `tracking` key and no
   * `landmark_indices`, because resolving those indices fail-closed is PR-4's
   * job and the materialiser sets the two TOGETHER or neither (plan §C.3a
   * guard 2). Absent therefore means "not scoring yet" — distinct from
   * `tracking: false`, which a goal annotation sets deliberately. Every
   * consumer already reads it as truthy-or-not (`HullVisualization`, the hull
   * calculation, the checkpoint-list badge), so absence is safe; what absence
   * must NOT be is silent, which is why the checkpoint list spells it out.
   */
  tracking?: boolean;
  description: string;
  color: string;
  frame?: number;
  timestamp?: number;
  activeFrames?: {
    mode: 'all' | 'range';
    startFrame?: number;
    endFrame?: number;
  };
  landmark_indices?: number[];
  landmark_names?: string[];
  functional_region?: string;  // Region ID (e.g. "lead_arm") for functional regions resolved at runtime
  /**
   * The coach's OWN words for the body points, kept apart from the anatomical
   * `landmark_names` that every display and mirror path reads (plan §C.3a
   * guard 1). Never rendered — it is authored intent in internal vocabulary.
   */
  authored_landmark_names?: string[];
  /**
   * PR-4. Present exactly when this region's body points were resolved to one
   * side of the body — which is also exactly when the region COUNTS in the
   * score. The four resolved fields (`landmark_indices`, `landmark_names`,
   * `tracking: true`, `resolution`) move together: the backend resolver either
   * sets all four or none, so the checkpoint list reads `resolution` as the
   * single "does this count?" signal and never infers one from a subset.
   *
   * `tracking: true` WITHOUT `resolution` stays valid and unchanged: that is a
   * region the coach drew on the canvas, whose indices came from the drawn box
   * rather than from a declared side.
   */
  resolution?: RegionResolution;
  /**
   * PR-4. Present exactly when the region could NOT be resolved: the
   * coach-facing what / why / how for the ask, in the backend's own words.
   * Rendered VERBATIM — the backend owns every sentence in it.
   */
  notice?: Notice;
  /**
   * PR-5. What this check does when the athlete is off — the backend's own
   * verdict (`shape`, which number decides it, and the failure angle in the
   * units the scorer compares against).
   *
   * Present on a materialised or accepted check; ABSENT on one the coach drew
   * in the studio and on anything built before PR-5. Absent renders nothing:
   * "we were not told" is not the same as "not a hard limit", and a card that
   * said the latter would be inventing a verdict the scorer never gave.
   */
  gate_summary?: GateSummary;
  notes?: string;  // Coach notes for this annotation
  target_phase?: string | null;  // Phase name this annotation belongs to
  spatial_goal?: SpatialGoal;      // Optional spatial goal (distance constraint)
  movement_goal?: TrajectoryConfig; // Optional movement goal (trajectory constraint)
  timing_goal?: TimingConfig;      // Optional timing goal (inter-landmark timing coordination)
  axis_goal?: AxisConfig;          // Optional axis alignment goal
  joint_angle_goal?: JointAngleConfig; // Optional joint angle goal
}

/**
 * Run the per-video annotation round-trip — and skip it when the check has no
 * box on the video yet.
 *
 * `POST /annotations/{videoId}` exists to map a drawn box to body points, and
 * its `AnnotationCreate.coordinates` is a required field. Since PR-3 of the
 * coach-input refactor an annotation can legitimately have NO position: one the
 * backend materialiser built from the coach's spoken instruction carries none
 * until the coach places it (owner decision D5). There is nothing to map from a
 * box that does not exist, so the call is SKIPPED rather than sent with a
 * made-up position — inventing one is the behaviour this refactor removed (the
 * old client gave every suggestion a box at (0.4, 0.4, 0.2 × 0.2)). The check
 * itself is kept either way: its body points came from the coach's words.
 *
 * Returns the server's response, or `null` when the call was skipped.
 */
async function saveCanvasAnnotation(
  videoId: string,
  annotation: Annotation,
  payload: Omit<SaveAnnotationPayload, 'coordinates'>,
): Promise<SaveAnnotationResponse | null> {
  const coordinates = annotation.coordinates;
  if (!coordinates) return null;
  return saveAnnotation(videoId, { ...payload, coordinates });
}

interface RegionAnnotatorProps {
  videoId: string;
  videoUrl: string;
  fps: number;
  totalFrames: number;
  videoWidth: number;
  videoHeight: number;
  annotations: Annotation[];
  onAnnotationsChange: (annotations: Annotation[]) => void;
  className?: string;
  templateHandedness?: 'right' | 'left' | 'ambidextrous';
  /**
   * Detected lead side from the backend (detect_lead_side_for_video() result).
   * Threaded from CreateTemplate → here → AnnotationCanvasOverlay for FE#4.
   * When provided, takes precedence over templateHandedness in canvas previews.
   */
  detectedLeadSide?: 'left' | 'right';
  phases?: Phase[];  // phases from step 2
  /** @deprecated Use videoId for auto-detection instead */
  facingDirection?: 'left' | 'right';
  /**
   * Template camera angle — the closed B3 vocabulary ('side' | 'frontal' |
   * 'diagonal' | 'other'). Passed to
   * TimingGoalAnnotation so kinetic-chain timing goals show a side-view advisory
   * banner (KS1). Optional — undefined = no banner.
   */
  cameraView?: string;
  /**
   * The saved template this checkpoint list belongs to, when there is one.
   *
   * Needed only by the "Place on the video" assist, which asks the backend
   * where a resolved region's body points sit in the reference video. While a
   * template is still being created there is no id to ask about — and no
   * resolved region either, because resolution happens when the template is
   * saved — so the action explains that instead of failing.
   */
  templateId?: string;
  /**
   * The side the coach declared as leading the movement, if they have. Drives
   * one line above the checkpoint list saying what will happen to the focus
   * areas that are still waiting on it. Distinct from `detectedLeadSide`,
   * which is a detection used for canvas previews — a declaration is the
   * coach's statement, and only a declaration resolves a region (plan §C.3a).
   */
  declaredLeadSide?: 'left' | 'right';
  /**
   * FE-3 — a checkpoint to OPEN for editing, by id, with its settings filled
   * in. Set by the page the moment a suggestion becomes a check.
   *
   * It goes through the component's OWN `handleEdit`, so what the coach sees is
   * identical to clicking the pencil themselves — same hydration, same panel
   * expansion, one code path. One-shot: `onCheckpointOpened` fires after it
   * opens and the page clears the request, so this never fights the coach's own
   * clicks.
   */
  openCheckpointId?: string | null;
  onCheckpointOpened?: () => void;
}

/**
 * Goal Score follow-ups batch 1 (frontend), item C: lets a SIBLING section
 * (`GoalAuthoringSection`, rendered as its own Card below this one in
 * `ScoringStudio.tsx` — never a child of this component, so it has no
 * access to this component's internal `poseData`/video container) request
 * one anatomical-point pick on THIS component's own video/skeleton overlay,
 * reusing the exact same `LandmarkPicker` hit-testing + `landmarkSelectionMode`
 * state machine the six existing Check panels already use — no second
 * hit-testing implementation.
 */
export interface RegionAnnotatorHandle {
  /**
   * Activates the shared landmark-picker overlay on this component's video.
   * `onSelect` fires once, with the plain landmark/joint name the coach
   * clicked, then selection mode returns to idle (same lifecycle as every
   * other selection mode below). Does not touch any of this component's own
   * Check state (e.g. `movementLandmark`) — this is a pure pass-through.
   */
  requestLandmarkSelection: (onSelect: (landmark: string) => void) => void;
  /**
   * Disarms a pending external pick. Idempotent; a no-op when no external
   * pick is outstanding (never touches the component's own Check selection
   * modes). The Goals section calls this on cancel/save/unmount so a stale
   * closure can never fire from a later click on the video (follow-ups batch
   * 1 red-team finding 1).
   */
  cancelLandmarkSelection: () => void;
}

/**
 * How close, in video pixels, a click has to be to a corner / endpoint to grab
 * it rather than the shape as a whole. Generous on purpose: a coach on a phone
 * is aiming with a fingertip, and the same number has to work for a mouse.
 */
const GRAB_HANDLE_PX = 16;

/** A drag in progress on an existing annotation (audit item FE-9). */
interface CanvasGesture {
  annotationId: string;
  /**
   * Which copy the drag is moving. While a check is open for editing, the
   * canvas shows the PENDING copy (dashed) and hides the saved one, so a drag
   * has to move the pending copy — moving the hidden saved one instead would
   * change geometry the coach cannot see and then lose it on Save.
   */
  target: 'saved' | 'pending';
  mode: 'move' | 'resize' | 'line-start' | 'line-end';
  /** Where the pointer went down, in video pixels. */
  origin: Point;
  /** The annotation's coordinates when the pointer went down. */
  original: NonNullable<Annotation['coordinates']>;
}

/** What the "Place on the video" action is doing for one annotation. */
type PlacementState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'blocked'; reason: PlacementBlockedReason };

/**
 * A focus area that is on the template but counting for nothing because its
 * side has not been settled.
 *
 * Reads the absence of `tracking` exactly as the materialise contract defines
 * it (`types/api.ts`, note 6): resolved regions set `tracking: true` together
 * with their indices, and a GOAL annotation sets `tracking: false`
 * deliberately, so neither of those is this state.
 */
function isFocusAreaAwaitingSide(ann: Annotation): boolean {
  return ann.tracking === undefined && !ann.resolution;
}

const RegionAnnotator = forwardRef<RegionAnnotatorHandle, RegionAnnotatorProps>(function RegionAnnotator({
  videoId,
  videoUrl,
  fps,
  totalFrames,
  videoWidth,
  videoHeight,
  annotations,
  onAnnotationsChange,
  className,
  templateHandedness = 'right',
  detectedLeadSide,
  phases,
  cameraView,
  templateId,
  declaredLeadSide,
  openCheckpointId,
  onCheckpointOpened,
}: RegionAnnotatorProps, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isVideoReady, setIsVideoReady] = useState(false);

  // Drawing state
  const [drawingMode, setDrawingMode] = useState<'rectangle' | 'circle' | 'line' | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [currentPoint, setCurrentPoint] = useState<Point | null>(null);

  // =====================================================
  // Independent Panel States
  // =====================================================
  const [bodyRegionExpanded, setBodyRegionExpanded] = useState(true);
  const [distanceGoalExpanded, setDistanceGoalExpanded] = useState(false);
  const [movementGoalExpanded, setMovementGoalExpanded] = useState(false);
  const [axisAlignmentExpanded, setAxisAlignmentExpanded] = useState(false);
  const [timingGoalExpanded, setTimingGoalExpanded] = useState(false);
  const [jointAngleGoalExpanded, setJointAngleGoalExpanded] = useState(false);

  // Body Region state
  const [pendingAnnotation, setPendingAnnotation] = useState<Annotation | null>(null);
  const [tracking, setTracking] = useState(true);
  const [selectedColor, setSelectedColor] = useState('#f59e0b');
  const [landmarkDetectionStatus, setLandmarkDetectionStatus] = useState<string | null>(null);

  // Distance Goal state
  const [spatialGoal, setSpatialGoal] = useState<SpatialGoal | undefined>(undefined);

  // Movement Goal state
  const [movementGoal, setMovementGoal] = useState<TrajectoryConfig | undefined>(undefined);
  const [movementLandmark, setMovementLandmark] = useState<string | undefined>(undefined);
  // Demo-path preview (travel/preview-path-points): the travel-distance
  // target editor's measured demo path for the picked landmark, drawn on
  // this authoring canvas while the coach is still picking the target.
  // null = nothing to draw (no travel editor open, or the current preview
  // abstained/errored/is stale for the current landmark+phase).
  const [travelPreviewPoints, setTravelPreviewPoints] = useState<Array<[number, number, number]> | null>(null);

  // Axis Alignment state
  const [axisConfig, setAxisConfig] = useState<AxisConfig | undefined>(undefined);

  // Axis Anchor Mode state (for arbitrary axis annotation via barycentric coords)
  const [axisAnchorMode, setAxisAnchorMode] = useState(false);
  const [pendingAnchorPoint, setPendingAnchorPoint] = useState<{ x: number; y: number } | null>(null);
  const [axisAnchors, setAxisAnchors] = useState<{ start: TemplateAnchor | null; end: TemplateAnchor | null }>({
    start: null,
    end: null,
  });

  // Timing Goal state
  const [timingGoal, setTimingGoal] = useState<TimingConfig | undefined>(undefined);

  // Joint Angle Goal state
  const [jointAngleGoal, setJointAngleGoal] = useState<JointAngleConfig | undefined>(undefined);

  // Unified landmark selection state
  type LandmarkSelectionMode =
    | { type: 'idle' }
    | { type: 'distance-source'; onSelect: (landmark: string) => void }
    | { type: 'distance-target'; onSelect: (landmark: string) => void }
    | { type: 'movement-landmark'; onSelect: (landmark: string) => void }
    | { type: 'axis-start'; onSelect: (landmarkIndex: number, landmarkName: string) => void }
    | { type: 'axis-end'; onSelect: (landmarkIndex: number, landmarkName: string) => void }
    | { type: 'timing-landmark-a'; onSelect: (landmark: string) => void }
    | { type: 'timing-landmark-b'; onSelect: (landmark: string) => void }
    // Goal Score follow-ups batch 1, item C: a pick requested by the sibling
    // GoalAuthoringSection via the imperative handle below — deliberately
    // distinct from 'movement-landmark' so it does NOT also trigger this
    // component's own `setMovementLandmark` side effect (see
    // `handleLandmarkSelected`).
    | { type: 'external-point'; onSelect: (landmark: string) => void };

  const [landmarkSelectionMode, setLandmarkSelectionMode] = useState<LandmarkSelectionMode>({ type: 'idle' });

  // Goal Score follow-ups batch 1, item C: expose the shared landmark-picker
  // overlay to the sibling GoalAuthoringSection Card, which renders no video
  // of its own (see RegionAnnotatorHandle's doc comment above).
  useImperativeHandle(ref, () => ({
    requestLandmarkSelection: (onSelect: (landmark: string) => void) => {
      setLandmarkSelectionMode({ type: 'external-point', onSelect });
    },
    cancelLandmarkSelection: () => {
      setLandmarkSelectionMode((current) => (current.type === 'external-point' ? { type: 'idle' } : current));
    },
  }), []);

  // Temporal annotation state (for body regions)
  const [frameMode, setFrameMode] = useState<'all' | 'range' | 'phase'>('all');
  const [rangeStart, setRangeStart] = useState(0);
  const [rangeEnd, setRangeEnd] = useState(totalFrames - 1);

  // Edit mode
  const [editingAnnotationId, setEditingAnnotationId] = useState<string | null>(null);

  // ── Direct manipulation of an existing annotation (audit item FE-9) ───────
  // Before PR-4 `handleMouseDown` returned early whenever no drawing tool was
  // picked, so a saved box could only be CHANGED by redrawing it from scratch.
  // A selected annotation is now draggable and resizable in place.
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null);
  const [gesture, setGesture] = useState<CanvasGesture | null>(null);

  // Per-annotation state for the "Place on the video" assist (owner decision D5).
  const [placementStates, setPlacementStates] = useState<Record<string, PlacementState>>({});

  // Error state for save operations
  const [saveError, setSaveError] = useState<string | null>(null);

  // Manual load trigger state
  const [showManualLoad, setShowManualLoad] = useState(false);

  // Hull visualization refs
  const hullVisualizationRef = useRef<HullVisualizationHandle>(null);
  const hullCanvasRef = useRef<HTMLCanvasElement>(null);

  // Pose data for landmark detection
  const [allPoseFrames, setAllPoseFrames] = useState<Map<number, PoseData>>(new Map());

  // Get pose data for current frame
  const poseData = useMemo(() => {
    if (allPoseFrames.has(currentFrame)) {
      return allPoseFrames.get(currentFrame) || null;
    }
    if (allPoseFrames.size > 0) {
      const firstKey = allPoseFrames.keys().next().value as number | undefined;
      if (firstKey !== undefined) {
        return allPoseFrames.get(firstKey) || null;
      }
    }
    return null;
  }, [allPoseFrames, currentFrame]);

  // Compute current phase based on currentFrame
  // Uses exclusive upper bound (<) to break ties at shared boundary frames
  // in favor of the next phase. Falls back to last phase for its final frame.
  const currentPhase = useMemo(() => {
    if (!phases || phases.length === 0) return null;
    const sorted = [...phases].sort((a, b) => a.start_frame - b.start_frame);
    return sorted.find(p => currentFrame >= p.start_frame && currentFrame < p.end_frame)
      ?? (currentFrame === sorted[sorted.length - 1]?.end_frame
        ? sorted[sorted.length - 1]
        : null);
  }, [phases, currentFrame]);

  // Update rangeEnd when totalFrames changes
  useEffect(() => {
    setRangeEnd(totalFrames - 1);
  }, [totalFrames]);

  // ── Adaptive Guide wiring (B-2026-0058) ──────────────────────────────────
  // Report the active tool to AdaptiveGuideContext whenever a panel expands.
  // These are side-effect-only calls — no existing annotation logic is changed.
  const { report: reportGuide } = useAdaptiveGuide();

  useEffect(() => {
    // Determine which tool is currently active based on the expanded panel.
    // Priority: most-recently-expanded panel wins (body-region first as it opens by default).
    let activeTool: string | null = null;
    if (bodyRegionExpanded) activeTool = 'body-region';
    else if (distanceGoalExpanded) activeTool = 'distance';
    else if (movementGoalExpanded) activeTool = 'movement';
    else if (axisAlignmentExpanded) activeTool = 'axis';
    else if (timingGoalExpanded) activeTool = 'timing';
    else if (jointAngleGoalExpanded) activeTool = 'joint-angle';
    reportGuide({ selectedTool: activeTool });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyRegionExpanded, distanceGoalExpanded, movementGoalExpanded, axisAlignmentExpanded, timingGoalExpanded, jointAngleGoalExpanded]);

  // Wire field-focus micro-guidance: when any focusable element inside a
  // [data-field-id] container gains focus, report that field ID to the context.
  // Uses document-level capture so it works for all descendant inputs/selects
  // without needing to modify each individual field.
  useEffect(() => {
    function handleFocusIn(e: FocusEvent) {
      const target = e.target as Element | null;
      if (!target) return;
      const container = target.closest('[data-field-id]') as HTMLElement | null;
      const fieldId = container?.dataset?.fieldId ?? null;
      reportGuide({ focusedField: fieldId });
    }
    function handleFocusOut() {
      // Clear focused field on blur so overview content restores.
      reportGuide({ focusedField: null });
    }
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('focusout', handleFocusOut, true);
    return () => {
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('focusout', handleFocusOut, true);
    };
  // reportGuide is stable (useCallback in context) — safe to omit from deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load pose data for landmark detection on mount
  useEffect(() => {
    const loadPoseData = async () => {
      try {
        // getPoseDataWithRetry() is typed RawPoseFrame[] (the loose shape shared
        // with overlay helpers that don't need frame_number), but the actual
        // /videos/{id}/pose success payload is the richer PoseData[] shape with
        // frame_number — which this map keys on. Assert the real shape rather
        // than widen PoseData/RawPoseFrame to paper over the mismatch.
        const data = await getPoseDataWithRetry(videoId) as unknown as
          Array<{ frame_number: number; landmarks: PoseData['landmarks'] }>;
        if (Array.isArray(data) && data.length > 0) {
          const poseMap = new Map<number, PoseData>();
          data.forEach((frame) => {
            if (frame.landmarks && frame.landmarks.length > 0) {
              poseMap.set(frame.frame_number, { landmarks: frame.landmarks });
            }
          });
          setAllPoseFrames(poseMap);
        }
      } catch {
        // Pose data loading is optional, silently continue if not available
      }
    };

    if (videoId) {
      loadPoseData();
    }
  }, [videoId]);

  // Initialize video element
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (video.readyState === 0) {
      video.load();
    }

    const checkTimeout = setTimeout(() => {
      if (video.readyState < 2) {
        video.currentTime = 0.1;
      }
    }, TIMEOUTS.VIDEO_LOAD_CHECK);

    return () => clearTimeout(checkTimeout);
  }, [videoUrl]);

  // Show manual load button if video doesn't load
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isVideoReady) {
        setShowManualLoad(true);
      }
    }, TIMEOUTS.MANUAL_LOAD_PROMPT);
    return () => clearTimeout(timeout);
  }, [isVideoReady]);

  // Handle predefined region selection (from BodyRegionAnnotation via PredefinedRegionSelector).
  // Resolution of functional regions (lead/trail side auto-detection) is done upstream in
  // PredefinedRegionSelector before onSelect is called, so region.landmarks are already correct here.
  const handleSelectPredefinedRegion = (region: PredefinedRegion & { functional_region?: string }) => {
    const landmarkIndices = region.landmarks || [];
    const landmarkNames = region.landmark_names || [];
    const importance = region.default_importance || 'medium';
    const colorToUse = region.color || getImportanceColor(importance);

    const predefinedAnnotation: Annotation = {
      id: crypto.randomUUID(),
      type: 'rectangle',
      coordinates: { x: 0, y: 0, width: 100, height: 100 },
      importance,
      tracking: true,
      description: region.name,
      color: colorToUse,
      frame: currentFrame,
      timestamp: currentFrame / fps,
      activeFrames: { mode: 'all' },
      landmark_indices: landmarkIndices,
      landmark_names: landmarkNames,
      functional_region: region.functional_region,
      notes: region.description,
    };

    setPendingAnnotation(predefinedAnnotation);
    setSelectedColor(colorToUse);
    setLandmarkDetectionStatus(`✓ ${landmarkIndices.length} landmark${landmarkIndices.length !== 1 ? 's' : ''} pre-mapped`);
    setTimeout(() => setLandmarkDetectionStatus(null), 3000);
  };

  // =====================================================
  // Save Handlers for Each Annotation Type
  // =====================================================

  /**
   * Put a saved annotation into the list — REPLACING the one being edited
   * rather than appending beside it (audit item FE-9).
   *
   * Why this exists. `handleEdit` used to pull the annotation OUT of the saved
   * list while the coach edited it, so every save handler could simply append
   * and the count came out right. The cost was that abandoning an edit — a
   * reload, a navigation, closing the tab, anything short of pressing Cancel —
   * destroyed the checkpoint. PR-4 leaves it in the list and marks it as being
   * edited, which moves the replacement here: without this, saving an edit
   * would leave the original behind and the coach would have two.
   *
   * The edited entry keeps its POSITION in the list. It does not keep its id:
   * five of the six save handlers mint a fresh one, exactly as they did when
   * the flow was remove-then-append, and changing that would alter what gets
   * written to the template on a path this PR is not about.
   */
  const commitAnnotation = (saved: Annotation): void => {
    const editedId = editingAnnotationId;
    if (editedId && annotations.some((a) => a.id === editedId)) {
      // The hull was drawn for the pre-edit geometry; drop it before the
      // replacement's own hull is calculated.
      hullVisualizationRef.current?.removeHull(editedId);
      onAnnotationsChange(annotations.map((a) => (a.id === editedId ? saved : a)));
      return;
    }
    onAnnotationsChange([...annotations, saved]);
  };

  /**
   * Save Body Region Annotation
   */
  const handleSaveBodyRegion = async (
    annotation: Annotation,
    label: string,
    notes: string,
    importance: number
  ): Promise<void> => {
    // Convert numeric importance to string
    const importanceStr: Annotation['importance'] =
      importance >= 85 ? 'critical' :
      importance >= 55 ? 'high' :
      importance >= 25 ? 'medium' : 'low';

    const annotationToSave: Annotation = {
      ...annotation,
      importance: importanceStr,
      tracking,
      description: label,
      color: selectedColor,
      notes: notes || undefined,
      activeFrames: frameMode === 'phase' && currentPhase
        ? { mode: 'range', startFrame: currentPhase.start_frame, endFrame: currentPhase.end_frame }
        : frameMode === 'range'
          ? { mode: 'range', startFrame: rangeStart, endFrame: rangeEnd }
          : { mode: 'all' },
      target_phase: frameMode === 'phase' && currentPhase ? currentPhase.name : null,
    };

    // Send to backend
    const backendPayload = {
      id: annotationToSave.id,
      type: annotationToSave.type,
      importance: annotationToSave.importance,
      emphasized_landmarks: annotationToSave.landmark_indices?.map(idx => String(idx)) || [],
      condition_text: annotationToSave.description,
      description: annotationToSave.description,
      timestamp: annotationToSave.timestamp || (annotationToSave.frame || 0) / fps,
      target_phase: annotationToSave.target_phase,
      active_frames: annotationToSave.activeFrames,
      // Functional region ID stored so evaluation can re-resolve lead side at score time
      ...(annotationToSave.functional_region && { functional_region: annotationToSave.functional_region }),
    };

    const result = await saveCanvasAnnotation(videoId, annotationToSave, backendPayload);

    if (result?.id) {
      annotationToSave.id = result.id;
    }

    if (result?.emphasized_landmarks) {
      annotationToSave.landmark_indices = result.emphasized_landmarks.map((idx: string) => parseInt(idx, 10));
    }

    // Add to annotations list
    commitAnnotation(annotationToSave);

    // Calculate hulls if tracking is enabled
    if (annotationToSave.tracking && annotationToSave.id) {
      setTimeout(async () => {
        await hullVisualizationRef.current?.calculateHulls(annotationToSave);
      }, 500);
    }

    // Clear pending annotation and editing state
    setEditingAnnotationId(null);
    setPendingAnnotation(null);
  };

  /**
   * Save Distance Goal Annotation
   */
  const handleSaveDistanceGoal = async (
    goal: SpatialGoal,
    label: string,
    notes: string,
    importance: number
  ): Promise<void> => {
    const importanceStr: Annotation['importance'] =
      importance >= 85 ? 'critical' :
      importance >= 55 ? 'high' :
      importance >= 25 ? 'medium' : 'low';

    // Create an annotation with spatial goal only
    const annotationToSave: Annotation = {
      id: crypto.randomUUID(),
      type: 'line',
      coordinates: { x: 0, y: 0, x2: 100, y2: 100 },
      importance: importanceStr,
      tracking: false,
      description: label,
      color: '#8b5cf6', // Purple for distance goals
      frame: currentFrame,
      timestamp: currentFrame / fps,
      activeFrames: frameMode === 'phase' && currentPhase
        ? { mode: 'range', startFrame: currentPhase.start_frame, endFrame: currentPhase.end_frame }
        : frameMode === 'range'
          ? { mode: 'range', startFrame: rangeStart, endFrame: rangeEnd }
          : { mode: 'all' },
      target_phase: frameMode === 'phase' && currentPhase ? currentPhase.name : null,
      notes: notes || undefined,
      spatial_goal: goal,
    };

    const backendPayload = {
      id: annotationToSave.id,
      type: annotationToSave.type,
      importance: annotationToSave.importance,
      emphasized_landmarks: [],
      condition_text: label,
      description: label,
      timestamp: annotationToSave.timestamp,
      target_phase: annotationToSave.target_phase,
      active_frames: annotationToSave.activeFrames,
      spatial_goal: goal,
    };

    const result = await saveCanvasAnnotation(videoId, annotationToSave, backendPayload);

    if (result?.id) {
      annotationToSave.id = result.id;
    }

    commitAnnotation(annotationToSave);
    setEditingAnnotationId(null);
    setSpatialGoal(undefined);
    setLandmarkSelectionMode({ type: 'idle' });
  };

  /**
   * Save Movement Goal Annotation
   */
  const handleSaveMovementGoal = async (
    goal: TrajectoryConfig,
    label: string,
    notes: string,
    importance: number
  ): Promise<void> => {
    const importanceStr: Annotation['importance'] =
      importance >= 85 ? 'critical' :
      importance >= 55 ? 'high' :
      importance >= 25 ? 'medium' : 'low';

    // Include selected landmark in movement goal
    const goalWithLandmark = movementLandmark
      ? { ...goal, tracked_landmark: movementLandmark }
      : goal;

    const annotationToSave: Annotation = {
      id: crypto.randomUUID(),
      type: 'rectangle',
      coordinates: { x: 0, y: 0, width: 50, height: 50 },
      importance: importanceStr,
      tracking: false,
      description: label,
      color: '#22c55e', // Green for movement goals
      frame: currentFrame,
      timestamp: currentFrame / fps,
      activeFrames: frameMode === 'phase' && currentPhase
        ? { mode: 'range', startFrame: currentPhase.start_frame, endFrame: currentPhase.end_frame }
        : frameMode === 'range'
          ? { mode: 'range', startFrame: rangeStart, endFrame: rangeEnd }
          : { mode: 'all' },
      target_phase: frameMode === 'phase' && currentPhase ? currentPhase.name : null,
      notes: notes || undefined,
      movement_goal: goalWithLandmark,
    };

    const backendPayload = {
      id: annotationToSave.id,
      type: annotationToSave.type,
      importance: annotationToSave.importance,
      emphasized_landmarks: [],
      condition_text: label,
      description: label,
      timestamp: annotationToSave.timestamp,
      target_phase: annotationToSave.target_phase,
      active_frames: annotationToSave.activeFrames,
      movement_goal: goalWithLandmark,
    };

    const result = await saveCanvasAnnotation(videoId, annotationToSave, backendPayload);

    if (result?.id) {
      annotationToSave.id = result.id;
    }

    commitAnnotation(annotationToSave);
    setEditingAnnotationId(null);
    setMovementGoal(undefined);
    setMovementLandmark(undefined);
    setLandmarkSelectionMode({ type: 'idle' });
  };

  /**
   * Save Timing Goal Annotation
   */
  const handleSaveTimingGoal = async (
    config: TimingConfig,
    label: string,
    notes: string,
    importance: number
  ): Promise<void> => {
    const importanceStr: Annotation['importance'] =
      importance >= 85 ? 'critical' :
      importance >= 55 ? 'high' :
      importance >= 25 ? 'medium' : 'low';

    const annotationToSave: Annotation = {
      id: crypto.randomUUID(),
      type: 'line',
      coordinates: { x: 0, y: 0, x2: 100, y2: 100 },
      importance: importanceStr,
      tracking: false,
      description: label,
      color: '#06B6D4', // Cyan for timing goals
      frame: currentFrame,
      timestamp: currentFrame / fps,
      activeFrames: frameMode === 'phase' && currentPhase
        ? { mode: 'range', startFrame: currentPhase.start_frame, endFrame: currentPhase.end_frame }
        : frameMode === 'range'
          ? { mode: 'range', startFrame: rangeStart, endFrame: rangeEnd }
          : { mode: 'all' },
      target_phase: frameMode === 'phase' && currentPhase ? currentPhase.name : null,
      notes: notes || undefined,
      timing_goal: config,
    };

    const backendPayload = {
      id: annotationToSave.id,
      type: annotationToSave.type,
      importance: annotationToSave.importance,
      emphasized_landmarks: [],
      condition_text: label,
      description: label,
      timestamp: annotationToSave.timestamp,
      target_phase: annotationToSave.target_phase,
      active_frames: annotationToSave.activeFrames,
      timing_goal: config,
    };

    setSaveError(null);
    try {
      const result = await saveCanvasAnnotation(videoId, annotationToSave, backendPayload);
      if (result?.id) {
        annotationToSave.id = result.id;
      }
    } catch (err) {
      // B-2026-0024: do NOT add to local state when the server rejected the
      // save — that produced silent data loss (looked saved locally, gone on
      // reload). Surface the failure and bail so the coach can retry.
      console.warn('Failed to persist timing goal to backend:', err);
      setSaveError('Failed to save timing goal — not saved. Please try again.');
      return;
    }

    commitAnnotation(annotationToSave);
    setEditingAnnotationId(null);
    setTimingGoal(undefined);
    setLandmarkSelectionMode({ type: 'idle' });
  };

  /**
   * Save Axis Alignment Goal Annotation
   */
  const handleSaveAxisAlignment = async (
    config: AxisConfig,
    label: string,
    notes: string,
    importance: number
  ): Promise<void> => {
    const importanceStr: Annotation['importance'] =
      importance >= 85 ? 'critical' :
      importance >= 55 ? 'high' :
      importance >= 25 ? 'medium' : 'low';

    const annotationToSave: Annotation = {
      id: crypto.randomUUID(),
      type: 'line',
      coordinates: { x: 0, y: 0, x2: 0, y2: 100 },
      importance: importanceStr,
      tracking: false,
      description: label,
      color: '#f59e0b', // Amber for axis alignment
      frame: currentFrame,
      timestamp: currentFrame / fps,
      activeFrames: frameMode === 'phase' && currentPhase
        ? { mode: 'range', startFrame: currentPhase.start_frame, endFrame: currentPhase.end_frame }
        : frameMode === 'range'
          ? { mode: 'range', startFrame: rangeStart, endFrame: rangeEnd }
          : { mode: 'all' },
      target_phase: frameMode === 'phase' && currentPhase ? currentPhase.name : null,
      notes: notes || undefined,
      axis_goal: config,
    };

    const backendPayload = {
      id: annotationToSave.id,
      type: annotationToSave.type,
      importance: annotationToSave.importance,
      emphasized_landmarks: [],
      condition_text: label,
      description: label,
      timestamp: annotationToSave.timestamp,
      target_phase: annotationToSave.target_phase,
      active_frames: annotationToSave.activeFrames,
      axis_goal: config,
    };

    setSaveError(null);
    try {
      const result = await saveCanvasAnnotation(videoId, annotationToSave, backendPayload);
      if (result?.id) {
        annotationToSave.id = result.id;
      }
    } catch (err) {
      // B-2026-0024: surface the failure and do not add to local state on a
      // server rejection (prevents silent data loss).
      console.warn('Failed to persist axis goal to backend:', err);
      setSaveError('Failed to save axis goal — not saved. Please try again.');
      return;
    }

    commitAnnotation(annotationToSave);
    setEditingAnnotationId(null);
    setAxisConfig(undefined);
    // Reset anchor state after save
    setAxisAnchorMode(false);
    setPendingAnchorPoint(null);
    setAxisAnchors({ start: null, end: null });
    setLandmarkSelectionMode({ type: 'idle' });
  };

  /**
   * Save Joint Angle Goal Annotation
   */
  const handleSaveJointAngleGoal = async (
    config: JointAngleConfig,
    label: string,
    notes: string,
    importance: number
  ): Promise<void> => {
    const importanceStr: Annotation['importance'] =
      importance >= 85 ? 'critical' :
      importance >= 55 ? 'high' :
      importance >= 25 ? 'medium' : 'low';

    const annotationToSave: Annotation = {
      id: crypto.randomUUID(),
      type: 'line',
      coordinates: { x: 0, y: 0, x2: 100, y2: 100 },
      importance: importanceStr,
      tracking: false,
      description: label,
      color: '#f59e0b', // Amber — matches joint angle preview color
      frame: currentFrame,
      timestamp: currentFrame / fps,
      activeFrames: frameMode === 'phase' && currentPhase
        ? { mode: 'range', startFrame: currentPhase.start_frame, endFrame: currentPhase.end_frame }
        : frameMode === 'range'
          ? { mode: 'range', startFrame: rangeStart, endFrame: rangeEnd }
          : { mode: 'all' },
      target_phase: frameMode === 'phase' && currentPhase ? currentPhase.name : null,
      notes: notes || undefined,
      joint_angle_goal: config,
    };

    const backendPayload = {
      id: annotationToSave.id,
      type: annotationToSave.type,
      importance: annotationToSave.importance,
      emphasized_landmarks: [],
      condition_text: label,
      description: label,
      timestamp: annotationToSave.timestamp,
      target_phase: annotationToSave.target_phase,
      active_frames: annotationToSave.activeFrames,
      joint_angle_goal: config,
    };

    setSaveError(null);
    try {
      const result = await saveCanvasAnnotation(videoId, annotationToSave, backendPayload);
      if (result?.id) {
        annotationToSave.id = result.id;
      }
    } catch (err) {
      // B-2026-0024: surface the failure and do not add to local state on a
      // server rejection (prevents silent data loss).
      console.warn('Failed to persist joint angle goal to backend:', err);
      setSaveError('Failed to save joint angle goal — not saved. Please try again.');
      return;
    }

    commitAnnotation(annotationToSave);
    setEditingAnnotationId(null);
    setJointAngleGoal(undefined);
  };

  // =====================================================
  // Playback Controls
  // =====================================================

  const handlePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    } else {
      video.play();
      setIsPlaying(true);
    }
  }, [isPlaying]);

  const handleFrameChange = useCallback((targetFrame: number) => {
    const video = videoRef.current;
    if (!video) return;

    video.pause();
    setIsPlaying(false);
    video.currentTime = (targetFrame + 0.001) / fps;
  }, [fps]);

  const handleSeekToFrame = useCallback((targetFrame: number) => {
    const video = videoRef.current;
    if (!video) return;

    video.pause();
    setIsPlaying(false);
    // Add sub-frame epsilon to defeat floating-point truncation in Math.floor(time * fps).
    // Without this, frame 50 at 30fps → 1.6666s → browser may land at 1.66659s →
    // Math.floor(1.66659 * 30) = 49 (one frame short).
    video.currentTime = (targetFrame + 0.001) / fps;
  }, [fps]);

  // =====================================================
  // Video Event Handlers
  // =====================================================

  const handleLoadStart = useCallback(() => {}, []);
  const handleLoadedMetadata = useCallback(() => {}, []);
  const handleLoadedData = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  }, []);

  const handleCanPlay = useCallback(() => {
    setIsVideoReady(true);
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setCurrentFrame(Math.floor(video.currentTime * fps));
  }, [fps]);

  const handleSeeked = useCallback(() => {
    setIsVideoReady(true);
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (canvas && video) {
      const ctx = canvas.getContext('2d');
      if (ctx && video.readyState >= 2) {
        ctx.clearRect(0, 0, videoWidth, videoHeight);
        ctx.drawImage(video, 0, 0, videoWidth, videoHeight);
      }
    }
  }, [videoWidth, videoHeight]);

  const handleError = useCallback((e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    console.error('❌ Video error:', e);
  }, []);

  // Get mouse position relative to canvas.
  // Memoized on [videoWidth, videoHeight] (the values it reads besides the
  // stable canvasRef) so the callbacks below that close over it always use
  // current-render scale factors instead of whichever were in scope the last
  // time their own deps changed.
  const getCanvasPoint = useCallback((e: React.MouseEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    // A canvas that is laid out at zero size (hidden tab, zero-height parent)
    // would make these scale factors Infinity and every coordinate NaN, which
    // then lands in an annotation. Fall back to 1:1 instead.
    const scaleX = rect.width > 0 ? videoWidth / rect.width : 1;
    const scaleY = rect.height > 0 ? videoHeight / rect.height : 1;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, [videoWidth, videoHeight]);

  // =====================================================
  // Direct manipulation of an existing annotation (FE-9)
  // =====================================================

  /**
   * What, if anything, a click at `point` grabs on `ann`.
   *
   * Corners / endpoints win over the body of the shape, so a click in the
   * overlap resizes rather than moves — that is the behaviour every drawing
   * tool has and the one a coach will expect.
   */
  const hitTestAnnotation = useCallback(
    (ann: Annotation, point: Point): CanvasGesture['mode'] | null => {
      const c = ann.coordinates;
      if (!c) return null;
      const near = (x: number, y: number) =>
        Math.abs(point.x - x) <= GRAB_HANDLE_PX && Math.abs(point.y - y) <= GRAB_HANDLE_PX;

      if (ann.type === 'rectangle' && c.width !== undefined && c.height !== undefined) {
        if (near(c.x + c.width, c.y + c.height)) return 'resize';
        const inside =
          point.x >= c.x && point.x <= c.x + c.width &&
          point.y >= c.y && point.y <= c.y + c.height;
        return inside ? 'move' : null;
      }

      if (ann.type === 'circle' && c.radius !== undefined) {
        const dist = Math.hypot(point.x - c.x, point.y - c.y);
        if (Math.abs(dist - c.radius) <= GRAB_HANDLE_PX) return 'resize';
        return dist < c.radius ? 'move' : null;
      }

      if (ann.type === 'line' && c.x2 !== undefined && c.y2 !== undefined) {
        if (near(c.x, c.y)) return 'line-start';
        if (near(c.x2, c.y2)) return 'line-end';
        // Distance from the point to the segment.
        const dx = c.x2 - c.x;
        const dy = c.y2 - c.y;
        const lenSq = dx * dx + dy * dy;
        if (lenSq === 0) return near(c.x, c.y) ? 'move' : null;
        const t = Math.max(0, Math.min(1, ((point.x - c.x) * dx + (point.y - c.y) * dy) / lenSq));
        const distance = Math.hypot(point.x - (c.x + t * dx), point.y - (c.y + t * dy));
        return distance <= GRAB_HANDLE_PX ? 'move' : null;
      }

      return null;
    },
    [],
  );

  /**
   * Which annotation a click lands on, in the order the coach sees them.
   *
   * The pending copy is drawn last and therefore sits on top, so it is tested
   * first. The annotation currently open for editing is SKIPPED in the saved
   * list because the canvas does not draw it — its pending copy stands in, and
   * only what is visible may be grabbed. After that, the already-selected one
   * wins over its neighbours, then the topmost drawn (last in the list).
   */
  const findAnnotationAtPoint = useCallback(
    (point: Point): { ann: Annotation; target: CanvasGesture['target']; mode: CanvasGesture['mode'] } | null => {
      if (pendingAnnotation?.coordinates) {
        const mode = hitTestAnnotation(pendingAnnotation, point);
        if (mode) return { ann: pendingAnnotation, target: 'pending', mode };
      }
      const selected = selectedAnnotationId
        ? annotations.find((a) => a.id === selectedAnnotationId)
        : undefined;
      if (selected && selected.id !== editingAnnotationId) {
        const mode = hitTestAnnotation(selected, point);
        if (mode) return { ann: selected, target: 'saved', mode };
      }
      for (let i = annotations.length - 1; i >= 0; i -= 1) {
        const ann = annotations[i];
        if (ann.id === selectedAnnotationId || ann.id === editingAnnotationId) continue;
        const mode = hitTestAnnotation(ann, point);
        if (mode) return { ann, target: 'saved', mode };
      }
      return null;
    },
    [annotations, pendingAnnotation, editingAnnotationId, selectedAnnotationId, hitTestAnnotation],
  );

  /** Apply a drag to one annotation's coordinates, clamped to the frame. */
  const applyGesture = useCallback(
    (g: CanvasGesture, point: Point): NonNullable<Annotation['coordinates']> => {
      const dx = point.x - g.origin.x;
      const dy = point.y - g.origin.y;
      const clampX = (v: number) => Math.max(0, Math.min(videoWidth, v));
      const clampY = (v: number) => Math.max(0, Math.min(videoHeight, v));
      const o = g.original;

      if (g.mode === 'move') {
        if (o.x2 !== undefined && o.y2 !== undefined) {
          return { ...o, x: clampX(o.x + dx), y: clampY(o.y + dy), x2: clampX(o.x2 + dx), y2: clampY(o.y2 + dy) };
        }
        return { ...o, x: clampX(o.x + dx), y: clampY(o.y + dy) };
      }
      if (g.mode === 'line-start') {
        return { ...o, x: clampX(o.x + dx), y: clampY(o.y + dy) };
      }
      if (g.mode === 'line-end' && o.x2 !== undefined && o.y2 !== undefined) {
        return { ...o, x2: clampX(o.x2 + dx), y2: clampY(o.y2 + dy) };
      }
      // resize
      if (o.radius !== undefined) {
        const radius = Math.max(GRAB_HANDLE_PX, Math.hypot(point.x - o.x, point.y - o.y));
        return { ...o, radius };
      }
      if (o.width !== undefined && o.height !== undefined) {
        return {
          ...o,
          width: Math.max(GRAB_HANDLE_PX, Math.min(videoWidth - o.x, o.width + dx)),
          height: Math.max(GRAB_HANDLE_PX, Math.min(videoHeight - o.y, o.height + dy)),
        };
      }
      return o;
    },
    [videoWidth, videoHeight],
  );

  // Mouse event handlers
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const point = getCanvasPoint(e);

    // No drawing tool picked: the click selects / grabs an existing annotation
    // instead of doing nothing (audit item FE-9 — before PR-4 this returned
    // early, so a box could only be changed by redrawing it from scratch).
    if (!drawingMode) {
      const hit = findAnnotationAtPoint(point);
      if (!hit) {
        setSelectedAnnotationId(null);
        return;
      }
      setSelectedAnnotationId(hit.ann.id);
      setGesture({
        annotationId: hit.ann.id,
        target: hit.target,
        mode: hit.mode,
        origin: point,
        original: hit.ann.coordinates as NonNullable<Annotation['coordinates']>,
      });
      return;
    }

    setIsDrawing(true);
    setStartPoint(point);
    setCurrentPoint(point);
  }, [drawingMode, getCanvasPoint, findAnnotationAtPoint]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (gesture) {
      const point = getCanvasPoint(e);
      // Computed from the gesture's ORIGIN and the geometry as it was when the
      // pointer went down, not from the previous move — so a move that reads a
      // not-yet-committed list still lands on the right coordinates.
      const next = applyGesture(gesture, point);
      if (gesture.target === 'pending') {
        setPendingAnnotation((p) => (p ? { ...p, coordinates: next } : p));
        return;
      }
      onAnnotationsChange(
        annotations.map((a) => (a.id === gesture.annotationId ? { ...a, coordinates: next } : a)),
      );
      return;
    }
    if (!isDrawing || !drawingMode) return;
    setCurrentPoint(getCanvasPoint(e));
  }, [gesture, getCanvasPoint, applyGesture, annotations, onAnnotationsChange, isDrawing, drawingMode]);

  const handleMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    // Ending a drag of an existing annotation (audit item FE-9). The new
    // geometry was already committed on each move, so there is nothing left to
    // write — the gesture just ends.
    //
    // Deliberately NOT re-run here: `findLandmarksInBounds`. Drawing a box
    // derives body points from it; dragging one must not, because a focus area
    // the backend resolved got its body points from the coach's declared side
    // (plan §C.3a), not from a box — re-deriving them from the geometry would
    // be a second, independent source for the same thing and would quietly
    // change what the region scores.
    if (gesture) {
      setGesture(null);
      return;
    }
    if (!isDrawing || !startPoint || !drawingMode) return;

    const endPoint = getCanvasPoint(e);
    let newAnnotation: Annotation | null = null;

    if (drawingMode === 'rectangle') {
      const width = endPoint.x - startPoint.x;
      const height = endPoint.y - startPoint.y;

      if (Math.abs(width) > 10 && Math.abs(height) > 10) {
        newAnnotation = {
          id: crypto.randomUUID(),
          type: 'rectangle',
          coordinates: {
            x: Math.min(startPoint.x, endPoint.x),
            y: Math.min(startPoint.y, endPoint.y),
            width: Math.abs(width),
            height: Math.abs(height),
          },
          importance: 'medium',
          tracking,
          description: '',
          color: selectedColor,
          frame: currentFrame,
          timestamp: currentFrame / fps,
          activeFrames: { mode: 'all' }
        };
      }
    } else if (drawingMode === 'circle') {
      const radius = Math.sqrt(
        Math.pow(endPoint.x - startPoint.x, 2) +
        Math.pow(endPoint.y - startPoint.y, 2)
      );

      if (radius > 10) {
        newAnnotation = {
          id: crypto.randomUUID(),
          type: 'circle',
          coordinates: { x: startPoint.x, y: startPoint.y, radius },
          importance: 'medium',
          tracking,
          description: '',
          color: selectedColor,
          frame: currentFrame,
          timestamp: currentFrame / fps,
          activeFrames: { mode: 'all' }
        };
      }
    } else if (drawingMode === 'line') {
      const distance = Math.sqrt(
        Math.pow(endPoint.x - startPoint.x, 2) +
        Math.pow(endPoint.y - startPoint.y, 2)
      );

      if (distance > 10) {
        newAnnotation = {
          id: crypto.randomUUID(),
          type: 'line',
          coordinates: { x: startPoint.x, y: startPoint.y, x2: endPoint.x, y2: endPoint.y },
          importance: 'medium',
          tracking,
          description: '',
          color: selectedColor,
          frame: currentFrame,
          timestamp: currentFrame / fps,
          activeFrames: { mode: 'all' }
        };
      }
    }

    if (newAnnotation) {
      // Auto-detect landmarks. `newAnnotation` was just drawn on the canvas a
      // few lines above, so it always has coordinates — the narrowing is for
      // the type, which allows a positionless annotation since PR-3 (see the
      // `coordinates` field's own comment).
      if (poseData && newAnnotation.coordinates) {
        const drawnCoordinates = newAnnotation.coordinates;
        try {
          setLandmarkDetectionStatus('Detecting landmarks...');
          const detectedIndices = findLandmarksInBounds(
            drawnCoordinates,
            newAnnotation.type,
            poseData,
            videoWidth,
            videoHeight
          );

          if (detectedIndices.length > 0) {
            const detectedNames = getLandmarkNames(detectedIndices);
            newAnnotation.landmark_indices = detectedIndices;
            newAnnotation.landmark_names = detectedNames;
            setLandmarkDetectionStatus(`✓ ${detectedIndices.length} landmark${detectedIndices.length !== 1 ? 's' : ''} detected`);
          } else {
            setLandmarkDetectionStatus('⚠ No landmarks detected in this region');
          }

          setTimeout(() => setLandmarkDetectionStatus(null), 3000);
        } catch {
          setLandmarkDetectionStatus('Failed to detect landmarks');
          setTimeout(() => setLandmarkDetectionStatus(null), 3000);
        }
      }

      setPendingAnnotation(newAnnotation);
    }

    setIsDrawing(false);
    setStartPoint(null);
    setCurrentPoint(null);
    setDrawingMode(null);
  }, [gesture, isDrawing, startPoint, drawingMode, tracking, selectedColor, currentFrame, fps, poseData, videoWidth, videoHeight, getCanvasPoint]);

  // =====================================================
  // "Place on the video" (owner decision D5)
  // =====================================================

  /**
   * Put a box on the canvas for a resolved focus area, derived from where its
   * body points actually sit in the reference video.
   *
   * The client NEVER invents a position. If the backend cannot derive one it
   * returns `box: null`, and that stays null here: the coach is told why and
   * pointed at the rectangle tool. Inventing a default is precisely the
   * behaviour owner decision D5 removed — the old client gave every suggestion
   * a box at (0.4, 0.4, 0.2 x 0.2), which read as a position someone had
   * chosen.
   *
   * The box arrives in normalised 0-1 canvas coordinates; annotations are kept
   * in video pixels, so it is scaled once, here. Once placed, the existing
   * edit / drag path owns it.
   */
  const handlePlaceOnVideo = async (id: string) => {
    if (!templateId) {
      setPlacementStates((s) => ({ ...s, [id]: { status: 'blocked', reason: 'template_not_saved' } }));
      return;
    }
    setPlacementStates((s) => ({ ...s, [id]: { status: 'loading' } }));
    try {
      const placement = await getRegionPlacement(templateId, id);
      if (!placement.box) {
        setPlacementStates((s) => ({ ...s, [id]: { status: 'blocked', reason: 'no_box' } }));
        return;
      }
      const { x, y, width, height } = placement.box;
      onAnnotationsChange(
        annotations.map((a) =>
          a.id === id
            ? {
                ...a,
                coordinates: {
                  x: x * videoWidth,
                  y: y * videoHeight,
                  width: width * videoWidth,
                  height: height * videoHeight,
                },
                ...(placement.frame_index !== null
                  ? { frame: placement.frame_index, timestamp: placement.frame_index / fps }
                  : {}),
              }
            : a,
        ),
      );
      setPlacementStates((s) => ({ ...s, [id]: { status: 'idle' } }));
      setSelectedAnnotationId(id);
      if (placement.frame_index !== null) handleSeekToFrame(placement.frame_index);
    } catch {
      setPlacementStates((s) => ({ ...s, [id]: { status: 'blocked', reason: 'request_failed' } }));
    }
  };

  const handleManualLoad = () => {
    const video = videoRef.current;
    if (video) {
      video.load();
      video.currentTime = 0;
      setShowManualLoad(false);
    }
  };

  // Delete annotation
  const handleDelete = (id: string) => {
    onAnnotationsChange(annotations.filter((a) => a.id !== id));
    hullVisualizationRef.current?.removeHull(id);
    // Deleting the annotation that is open for editing or selected on the
    // canvas has to clear those, or the panel keeps offering to save something
    // that is no longer on the template.
    if (editingAnnotationId === id) {
      setEditingAnnotationId(null);
      setPendingAnnotation(null);
    }
    if (selectedAnnotationId === id) setSelectedAnnotationId(null);
  };

  /**
   * Edit annotation — load it into the editing panel, pre-populating fields.
   *
   * It STAYS in the saved list (audit item FE-9). It used to be pulled out for
   * the duration of the edit, which meant anything other than pressing Save or
   * Cancel — a reload, a navigation, a closed tab — silently destroyed the
   * checkpoint the coach had already created. It is marked as being edited
   * instead, `commitAnnotation` replaces it in place on save, and Cancel now
   * has nothing to restore.
   */
  const handleEdit = (id: string) => {
    const ann = annotations.find((a) => a.id === id);
    if (!ann) return;
    // Mark as editing and load into editing panel
    setEditingAnnotationId(id);
    setSelectedAnnotationId(id);
    setPendingAnnotation(ann);
    setSelectedColor(ann.color || '#22c55e');

    // Phase-scope restore (backlog/studio-handle-edit-does-not-restore-phase-
    // scope.md): `target_phase` is the SHARED field every goal type stores
    // its phase scope in -- not movement-goal-specific -- so this runs for
    // every annotation, before the per-goal-type hydration below. Without
    // it, reopening a phase-scoped goal (e.g. a travel check) showed
    // `frameMode` as whatever a PREVIOUS edit in this session left it at,
    // so its demo preview re-measured over the wrong window until the coach
    // re-picked the phase chip by hand.
    //
    // `currentPhase` is DERIVED from `currentFrame` (see the useMemo above),
    // not its own piece of state -- there is nothing to "set" directly, so
    // restoring the scope means moving the video cursor into the saved
    // phase's own frame range and letting `currentPhase` recompute to match.
    //
    // Custom-range restore (studio PR-B leftover item 2): the sibling case to
    // the phase restore above. A goal scoped to a custom frame RANGE (not a
    // phase) stores `target_phase: null` and `activeFrames: { mode: 'range',
    // startFrame, endFrame }` -- the same shape every save handler below
    // writes for `frameMode === 'range'`. `target_phase` is checked FIRST
    // because a phase-scoped save ALSO writes `activeFrames.mode === 'range'`
    // (with the phase's own bounds, not the coach-typed ones) -- so without
    // this ordering a phase-scoped goal would be mis-read as a custom range.
    if (ann.target_phase && phases && phases.length > 0) {
      const matchedPhase = phases.find(
        (p) => p.name.toLowerCase() === ann.target_phase!.toLowerCase(),
      );
      if (matchedPhase) {
        setFrameMode('phase');
        setCurrentFrame(matchedPhase.start_frame);
      } else {
        // The saved phase was renamed or deleted since -- fall back to
        // whole-clip rather than silently keeping a stale 'phase'/'range'
        // selection from editing a different annotation moments earlier.
        setFrameMode('all');
      }
    } else if (
      ann.activeFrames?.mode === 'range' &&
      ann.activeFrames.startFrame !== undefined &&
      ann.activeFrames.endFrame !== undefined
    ) {
      setFrameMode('range');
      setRangeStart(ann.activeFrames.startFrame);
      setRangeEnd(ann.activeFrames.endFrame);
    } else {
      setFrameMode('all');
    }

    // Hydrate panel-specific goal state from the annotation so the
    // panel's form fields (landmarks, config) are pre-populated.
    if (ann.spatial_goal) setSpatialGoal(ann.spatial_goal as SpatialGoal);
    if (ann.movement_goal) {
      const movementGoalConfig = ann.movement_goal as TrajectoryConfig;
      setMovementGoal(movementGoalConfig);
      // T-7 fix (docs/coach_input/SPIKE_TRAJECTORY_CHECKS.md §1.3): the
      // landmark lives on `movement_goal.tracked_landmark` (see
      // handleSaveMovementGoal's `goalWithLandmark` below), not a separate
      // top-level annotation field. Restoring `movementGoal` alone left
      // `movementLandmark` at whatever the panel's last reset left it
      // (usually `undefined`), so `MovementGoalAnnotation`'s
      // `hasCompleteMovementGoal` — which requires a truthy
      // `selectedLandmark` — stayed false and Save never re-enabled.
      if (movementGoalConfig.tracked_landmark) {
        setMovementLandmark(movementGoalConfig.tracked_landmark);
      }
    }
    if (ann.axis_goal) setAxisConfig(ann.axis_goal as AxisConfig);
    if (ann.timing_goal) setTimingGoal(ann.timing_goal as TimingConfig);
    if (ann.joint_angle_goal) setJointAngleGoal(ann.joint_angle_goal as JointAngleConfig);

    // Auto-expand the relevant panel based on annotation type.
    if (ann.joint_angle_goal) {
      setJointAngleGoalExpanded(true);
    } else if (ann.axis_goal) {
      setAxisAlignmentExpanded(true);
    } else if (ann.timing_goal) {
      setTimingGoalExpanded(true);
    } else if (ann.spatial_goal?.enabled) {
      setDistanceGoalExpanded(true);
    } else if (ann.movement_goal?.enabled) {
      setMovementGoalExpanded(true);
    } else {
      setBodyRegionExpanded(true);
    }
  };

  /**
   * FE-3 — open the checkpoint the page just asked for.
   *
   * Goes through `handleEdit` above rather than reimplementing any of it, so a
   * check opened this way is hydrated and expanded exactly as if the coach had
   * clicked its pencil — one code path, no second copy of the hydration rules
   * to drift.
   *
   * Two guards. `annotations.some(...)` waits for the annotation to actually be
   * in the list: the page sets the id and the annotations in the same update,
   * and opening an id that has not arrived yet would silently do nothing. The
   * ref makes it ONE-SHOT per id, so the coach can close or switch the panel
   * and this will not drag them back.
   */
  const openedCheckpointRef = useRef<string | null>(null);
  // `handleEdit` is re-created on every render (it closes over a dozen setters
  // and the annotation list), so holding the LATEST one in a ref is what keeps
  // the effect below keyed on the request rather than on the render.
  const handleEditRef = useRef(handleEdit);
  useEffect(() => {
    handleEditRef.current = handleEdit;
  });
  useEffect(() => {
    if (!openCheckpointId) {
      openedCheckpointRef.current = null;
      return;
    }
    if (openedCheckpointRef.current === openCheckpointId) return;
    if (!annotations.some((a) => a.id === openCheckpointId)) return;
    openedCheckpointRef.current = openCheckpointId;
    handleEditRef.current(openCheckpointId);
    onCheckpointOpened?.();
  }, [openCheckpointId, annotations, onCheckpointOpened]);

  // =====================================================
  // Landmark Selection Handlers
  // =====================================================

  const handleRequestDistanceSourceSelection = useCallback((onSelect: (landmark: string) => void) => {
    setLandmarkSelectionMode({ type: 'distance-source', onSelect });
  }, []);

  const handleRequestDistanceTargetSelection = useCallback((onSelect: (landmark: string) => void) => {
    setLandmarkSelectionMode({ type: 'distance-target', onSelect });
  }, []);

  const handleRequestMovementLandmarkSelection = useCallback((onSelect: (landmark: string) => void) => {
    setLandmarkSelectionMode({ type: 'movement-landmark', onSelect });
  }, []);

  const handleRequestAxisLandmarkSelection = useCallback((
    which: 'landmark_start' | 'landmark_end',
    callback: (landmarkIndex: number, landmarkName: string) => void
  ) => {
    if (which === 'landmark_start') {
      setLandmarkSelectionMode({ type: 'axis-start', onSelect: callback });
    } else {
      setLandmarkSelectionMode({ type: 'axis-end', onSelect: callback });
    }
  }, []);

  const handleRequestTimingLandmarkASelection = useCallback((onSelect: (landmark: string) => void) => {
    setLandmarkSelectionMode({ type: 'timing-landmark-a', onSelect });
  }, []);

  const handleRequestTimingLandmarkBSelection = useCallback((onSelect: (landmark: string) => void) => {
    setLandmarkSelectionMode({ type: 'timing-landmark-b', onSelect });
  }, []);

  const handleCancelLandmarkSelection = useCallback(() => {
    setLandmarkSelectionMode({ type: 'idle' });
  }, []);

  // =====================================================
  // Axis Anchor Mode Handlers
  // =====================================================

  const handleToggleAnchorMode = useCallback(() => {
    setAxisAnchorMode((prev) => {
      if (prev) {
        // Toggling off — reset anchor state inside the updater to avoid stale closure
        setPendingAnchorPoint(null);
        setAxisAnchors({ start: null, end: null });
      }
      return !prev;
    });
  }, []);

  const handleResetAnchors = useCallback(() => {
    setPendingAnchorPoint(null);
    setAxisAnchors({ start: null, end: null });
  }, []);

  const handleAxisAnchorClick = useCallback(
    (normalizedX: number, normalizedY: number) => {
      if (!poseData?.landmarks) return;

      const anchor = createAnchorFromClick(
        normalizedX,
        normalizedY,
        poseData.landmarks,
        LANDMARK_NAMES,
        currentFrame,
      );

      if (!anchor) {
        // Degenerate: not enough visible landmarks nearby
        console.warn('[AxisAnchor] Could not create anchor: insufficient visible landmarks near click');
        return;
      }

      setAxisAnchors((prev) => {
        if (!prev.start) {
          // Place the first anchor and show the pending point
          setPendingAnchorPoint({ x: normalizedX, y: normalizedY });
          return { ...prev, start: anchor };
        } else {
          // Place the second anchor and clear the pending point
          setPendingAnchorPoint(null);
          return { ...prev, end: anchor };
        }
      });
    },
    [poseData, currentFrame],
  );

  const handleLandmarkSelected = useCallback((landmark: string, landmarkIndex?: number) => {
    if (landmarkSelectionMode.type !== 'idle') {
      // For axis selection, pass both index and name
      if (landmarkSelectionMode.type === 'axis-start' || landmarkSelectionMode.type === 'axis-end') {
        const idx = landmarkIndex ?? 0; // Default to 0 if not provided
        landmarkSelectionMode.onSelect(idx, landmark);
      } else {
        // For other selection types, just pass the landmark name
        landmarkSelectionMode.onSelect(landmark);
      }

      // For movement goal, also update local state
      if (landmarkSelectionMode.type === 'movement-landmark') {
        setMovementLandmark(landmark);
      }

      // Cancel selection mode after selection
      setLandmarkSelectionMode({ type: 'idle' });
    }
  }, [landmarkSelectionMode]);

  return (
    <div className={cn('grid grid-cols-1 lg:grid-cols-3 gap-6', className)}>
      {/* B-2026-0024: surface annotation save failures instead of swallowing
          them in console.warn. The annotation is NOT added to local state when
          this shows, so the coach knows to retry. */}
      {saveError && (
        <div className="lg:col-span-3 flex items-center justify-between gap-3 rounded-lg border border-danger/40 bg-danger/10 px-4 py-2.5 text-sm text-danger">
          <span>{saveError}</span>
          <button
            type="button"
            onClick={() => setSaveError(null)}
            className="text-xs font-medium underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}
      {/* Left Column: Inspector Panels grouped into three decision families */}
      <div className="lg:col-span-1 space-y-3" data-testid="inspector-column">
        {/* Annotation Scope Selector — applies to all panels below */}
        <div className="rounded-lg border border-border-subtle bg-dark-card p-3 space-y-2" data-field-id={FIELD_IDS.PHASE_SELECTOR}>
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
              Annotation Scope
            </label>
            {frameMode !== 'all' && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-info/10 text-info font-medium">
                {frameMode === 'phase' ? currentPhase?.name : `Frames ${rangeStart}-${rangeEnd}`}
              </span>
            )}
          </div>

          <div className="flex gap-1">
            <button
              onClick={() => setFrameMode('all')}
              className={cn(
                'flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors',
                frameMode === 'all'
                  ? 'bg-dark-elevated-2 text-text-primary'
                  : 'bg-dark-elevated-1 text-text-secondary hover:bg-dark-elevated-2'
              )}
            >
              Entire Video
            </button>
            <button
              onClick={() => {
                if (currentPhase) {
                  setFrameMode('phase');
                }
              }}
              disabled={!currentPhase}
              className={cn(
                'flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors',
                frameMode === 'phase'
                  ? 'bg-accent text-text-inverse'
                  : currentPhase
                    ? 'bg-dark-elevated-1 text-text-secondary hover:bg-dark-elevated-2'
                    : 'bg-dark-elevated-1 text-text-muted cursor-not-allowed'
              )}
            >
              Current Phase
            </button>
            <button
              onClick={() => setFrameMode('range')}
              className={cn(
                'flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors',
                frameMode === 'range'
                  ? 'bg-accent text-text-inverse'
                  : 'bg-dark-elevated-1 text-text-secondary hover:bg-dark-elevated-2'
              )}
            >
              Custom Range
            </button>
          </div>

          {/* Current phase info */}
          {frameMode === 'phase' && currentPhase && (
            <div className="flex items-center gap-2 text-xs text-info bg-info/10 rounded-md p-2">
              <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                Annotations will be scoped to <strong>{currentPhase.name}</strong> (frames {currentPhase.start_frame}–{currentPhase.end_frame})
              </span>
            </div>
          )}

          {/* No phase warning */}
          {frameMode === 'phase' && !currentPhase && (
            <div className="text-xs text-warning bg-warning/10 rounded-md p-2">
              Move the video cursor to a phase to scope annotations to it
            </div>
          )}

          {/* Custom range inputs */}
          {frameMode === 'range' && (
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <label className="text-[10px] text-text-muted">Start Frame</label>
                <input
                  type="number"
                  min={0}
                  max={totalFrames - 1}
                  value={rangeStart}
                  onChange={(e) => setRangeStart(Math.max(0, Math.min(parseInt(e.target.value) || 0, rangeEnd - 1)))}
                  className="w-full px-2 py-1 text-xs border border-border-subtle bg-dark-elevated-1 text-text-primary rounded"
                />
              </div>
              <div className="flex-1">
                <label className="text-[10px] text-text-muted">End Frame</label>
                <input
                  type="number"
                  min={0}
                  max={totalFrames - 1}
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(Math.max(rangeStart + 1, Math.min(parseInt(e.target.value) || 0, totalFrames - 1)))}
                  className="w-full px-2 py-1 text-xs border border-border-subtle bg-dark-elevated-1 text-text-primary rounded"
                />
              </div>
            </div>
          )}

          {/* General help text */}
          {frameMode === 'all' && (
            <p className="text-[10px] text-text-muted">
              All annotations below will apply to the entire video duration
            </p>
          )}
        </div>

        {/* Editing banner — shown when modifying an existing annotation */}
        {editingAnnotationId && pendingAnnotation && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-2.5 rounded-lg border border-amber-500/40 bg-amber-500/10">
            <div className="flex items-start gap-2">
              <PencilSquareIcon className="h-4 w-4 mt-0.5 text-amber-400 flex-shrink-0" />
              <div>
                <span className="text-sm font-medium text-amber-300">
                  Editing: {pendingAnnotation.description || 'Unnamed annotation'}
                </span>
                {/*
                  While the panel is open the canvas shows this copy of the
                  check, dashed — and PR-4 makes it draggable, so say so
                  (audit item FE-9: nothing announced that a box could move).
                */}
                {pendingAnnotation.coordinates && (
                  <p className="text-xs text-amber-200/80" data-testid="editing-drag-hint">
                    {SELECTED_ON_CANVAS_HINT}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => {
                // Cancel edit — nothing to restore. The annotation never left
                // the saved list (audit item FE-9), so cancelling just closes
                // the panel and the coach keeps what they had.
                setEditingAnnotationId(null);
                setPendingAnnotation(null);
              }}
              className="text-xs text-text-secondary hover:text-text-primary transition-colors"
            >
              Cancel
            </button>
          </div>
        )}

        {/* ── FOCUS family ─────────────────────────────────────────────────────
            Body-region emphasis: which areas of the body to watch closely.
            Adding new region-type tools here keeps them in the Focus family.    */}
        <div data-field-id={FIELD_IDS.BODY_REGION_IMPORTANCE} data-guide-section="studio-panel-body-region">
        <InspectorSection
          id="focus"
          label="Focus"
          accentClass="text-blue-400"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
              <circle cx="12" cy="12" r="3" />
              <path strokeLinecap="round" d="M12 3v2m0 14v2M3 12h2m14 0h2" />
            </svg>
          }
        >
          <BodyRegionAnnotation
            isExpanded={bodyRegionExpanded}
            onToggle={() => setBodyRegionExpanded(!bodyRegionExpanded)}
            panelNumber={1}
            totalPanels={1}
            pendingAnnotation={pendingAnnotation}
            onPendingAnnotationChange={setPendingAnnotation}
            drawingMode={drawingMode === 'line' ? null : drawingMode}
            onDrawingModeChange={(mode) => setDrawingMode(mode)}
            onSelectPredefinedRegion={handleSelectPredefinedRegion}
            templateHandedness={templateHandedness}
            videoId={videoId}
            tracking={tracking}
            onTrackingChange={setTracking}
            onSave={handleSaveBodyRegion}
            landmarkDetectionStatus={landmarkDetectionStatus}
          />
        </InspectorSection>
        </div>

        {/* ── CUSTOM GOALS family ───────────────────────────────────────────────
            Coach-defined movement checkpoints: distance, trajectory, axis angle,
            timing coordination, and joint angle.
            To add a new goal type: insert a new panel component here — no
            structural changes to InspectorSection or RegionAnnotator are needed.  */}
        <div data-guide-section="studio-panel-custom-goals">
        <InspectorSection
          id="custom-goals"
          label="Custom Goals"
          accentClass="text-violet-400"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        >
          {/* Distance Goal */}
          <DistanceGoalAnnotation
            isExpanded={distanceGoalExpanded}
            onToggle={() => setDistanceGoalExpanded(!distanceGoalExpanded)}
            panelNumber={1}
            totalPanels={5}
            spatialGoal={spatialGoal}
            onSpatialGoalChange={setSpatialGoal}
            poseData={poseData}
            videoWidth={videoWidth}
            videoHeight={videoHeight}
            videoContainerRef={videoContainerRef}
            phases={phases}
            currentPhase={currentPhase}
            onSave={handleSaveDistanceGoal}
            onRequestSourceSelection={handleRequestDistanceSourceSelection}
            onRequestTargetSelection={handleRequestDistanceTargetSelection}
            isSelectionActive={landmarkSelectionMode.type !== 'idle' && !landmarkSelectionMode.type.startsWith('distance')}
          />

          {/* Movement Goal */}
          <MovementGoalAnnotation
            isExpanded={movementGoalExpanded}
            onToggle={() => setMovementGoalExpanded(!movementGoalExpanded)}
            panelNumber={2}
            totalPanels={5}
            movementGoal={movementGoal}
            onMovementGoalChange={setMovementGoal}
            phases={phases}
            currentPhase={currentPhase}
            targetPhase={frameMode === 'phase' && currentPhase ? currentPhase.name : null}
            templateId={templateId}
            onSave={handleSaveMovementGoal}
            onPreviewTrajectoryChange={setTravelPreviewPoints}
            onRequestLandmarkSelection={handleRequestMovementLandmarkSelection}
            selectedLandmark={movementLandmark}
            isSelectionActive={landmarkSelectionMode.type !== 'idle' && landmarkSelectionMode.type !== 'movement-landmark'}
          />

          {/* Axis Alignment Goal */}
          <AxisLineAnnotation
            isExpanded={axisAlignmentExpanded}
            onToggle={() => setAxisAlignmentExpanded(!axisAlignmentExpanded)}
            panelNumber={3}
            totalPanels={5}
            axisConfig={axisConfig}
            onAxisConfigChange={setAxisConfig}
            phases={phases}
            currentPhase={currentPhase}
            onSave={handleSaveAxisAlignment}
            onRequestLandmarkSelection={handleRequestAxisLandmarkSelection}
            isSelectingLandmark={
              landmarkSelectionMode.type === 'axis-start' ? 'landmark_start' :
              landmarkSelectionMode.type === 'axis-end' ? 'landmark_end' :
              null
            }
            isSelectionActive={landmarkSelectionMode.type !== 'idle' && !landmarkSelectionMode.type.startsWith('axis')}
            anchorMode={axisAnchorMode}
            onToggleAnchorMode={handleToggleAnchorMode}
            anchors={axisAnchors}
            onResetAnchors={handleResetAnchors}
            pendingAnchorPoint={pendingAnchorPoint}
            templatePoseFrames={allPoseFrames}
            /* PR-5 — the backend's verdict for the check being edited, so the
               hard-limit control can say WHOSE number decides a failure. Only
               the check actually open for editing has one; a new check has
               none, and the control then describes its own unsaved state. */
            gateSummary={
              editingAnnotationId ? pendingAnnotation?.gate_summary ?? null : null
            }
          />

          {/* Timing Sequence Goal */}
          <TimingGoalAnnotation
            isExpanded={timingGoalExpanded}
            onToggle={() => setTimingGoalExpanded(!timingGoalExpanded)}
            panelNumber={4}
            totalPanels={5}
            timingConfig={timingGoal}
            onTimingConfigChange={setTimingGoal}
            phases={phases}
            currentPhase={currentPhase}
            onSave={handleSaveTimingGoal}
            onRequestLandmarkASelection={handleRequestTimingLandmarkASelection}
            onRequestLandmarkBSelection={handleRequestTimingLandmarkBSelection}
            isSelectionActive={landmarkSelectionMode.type !== 'idle' && !landmarkSelectionMode.type.startsWith('timing')}
            cameraView={cameraView}
          />

          {/* Joint Angle Goal (B-2026-0054) */}
          <JointAngleGoalAnnotation
            isExpanded={jointAngleGoalExpanded}
            onToggle={() => setJointAngleGoalExpanded(!jointAngleGoalExpanded)}
            panelNumber={5}
            totalPanels={5}
            jointAngleConfig={jointAngleGoal}
            onJointAngleConfigChange={setJointAngleGoal}
            phases={phases}
            currentPhase={currentPhase}
            onSave={handleSaveJointAngleGoal}
          />
        </InspectorSection>
        </div>{/* end studio-panel-custom-goals */}

        {/* ── SCORING family ────────────────────────────────────────────────────
            Score weights and form baseline live in step 4 (Scoring Studio).
            This nav-hint points coaches there without duplicating controls.
            When B-2026-0055 (Scoring Emphasis panel) lands, it will appear here
            as a child of this InspectorSection — no structural changes needed.   */}
        <div data-field-id={FIELD_IDS.SCORING_STUDIO_PANEL} data-guide-section="studio-panel-scoring">
        <InspectorSection
          id="scoring"
          label="Scoring"
          accentClass="text-emerald-400"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
        >
          {/* Scoring controls live in step 4 — Scoring Studio.
              B-2026-0055 (Scoring Emphasis panel) will slot here once implemented. */}
          <div className="px-1 py-2 text-xs text-text-muted space-y-1">
            <p className="font-medium text-text-secondary">
              Body Form &amp; Economy of Motion weights
            </p>
            <p>
              Score weighting is configured in the{' '}
              <span className="text-emerald-400 font-medium">Scoring Studio</span>{' '}
              (next step). Continue there to set how much each body region contributes
              to the final score.
            </p>
          </div>
        </InspectorSection>
        </div>

        {/* Hull Tracking Visualization (internal, no section header) */}
        <HullVisualization
          ref={hullVisualizationRef}
          videoId={videoId}
          annotations={annotations}
          currentFrame={currentFrame}
          videoWidth={videoWidth}
          videoHeight={videoHeight}
          canvasRef={hullCanvasRef}
        />
      </div>

      {/* Right Column: Video Player */}
      <div className="lg:col-span-2 space-y-4">
        {/* Manual load alert */}
        {showManualLoad && !isVideoReady && (
          <Alert variant="warning">
            <div className="flex items-center justify-between">
              <span>Video is taking longer than expected to load.</span>
              <Button onClick={handleManualLoad} size="sm">Retry Load</Button>
            </div>
          </Alert>
        )}

        {/* Video with Canvas Overlay */}
        <div
          ref={videoContainerRef}
          className="relative border-2 border-border-strong rounded-lg overflow-hidden bg-black"
          data-field-id={FIELD_IDS.GOAL_LANDMARK_PICKER}
        >
          <video
            ref={videoRef}
            src={videoUrl}
            className="w-full"
            preload="auto"
            playsInline
            muted
            crossOrigin="anonymous"
            onLoadStart={handleLoadStart}
            onLoadedMetadata={handleLoadedMetadata}
            onLoadedData={handleLoadedData}
            onCanPlay={handleCanPlay}
            onTimeUpdate={handleTimeUpdate}
            onSeeked={handleSeeked}
            onError={handleError}
            style={{ aspectRatio: `${videoWidth}/${videoHeight}` }}
          />
          <AnnotationCanvasOverlay
            canvasRef={canvasRef}
            videoRef={videoRef}
            annotations={annotations}
            pendingAnnotation={pendingAnnotation}
            selectedAnnotationId={selectedAnnotationId}
            editingAnnotationId={editingAnnotationId}
            drawingMode={drawingMode}
            isDrawing={isDrawing}
            startPoint={startPoint}
            currentPoint={currentPoint}
            selectedColor={selectedColor}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            videoWidth={videoWidth}
            videoHeight={videoHeight}
            currentFrame={currentFrame}
            isVideoReady={isVideoReady}
            axisConfig={axisConfig}
            poseData={poseData}
            axisAnchorMode={axisAnchorMode}
            onAxisAnchorClick={handleAxisAnchorClick}
            pendingAnchorPoint={pendingAnchorPoint}
            axisAnchors={axisAnchors}
            templateHandedness={templateHandedness}
            detectedLeadSide={detectedLeadSide}
            jointAngleConfig={jointAngleGoalExpanded ? jointAngleGoal : undefined}
          />

          {/* Hull overlay canvas */}
          <canvas
            ref={hullCanvasRef}
            width={videoWidth}
            height={videoHeight}
            className="absolute top-0 left-0 w-full h-full pointer-events-none"
            style={{ aspectRatio: `${videoWidth}/${videoHeight}` }}
          />

          {/* Demo-path preview (travel/preview-path-points): the travel-
              distance target editor's own demo path for the picked point,
              drawn on this (reference-clip) authoring canvas while the
              coach is still picking the target. Reuses GoalOverlayLayer's
              trajectory-drawing code path rather than a new renderer --
              goalResults is empty here since there is no scored result
              yet; only `previewTrajectory` feeds this instance. */}
          <GoalOverlayLayer
            goalResults={{}}
            poseData={undefined}
            currentFrame={currentFrame}
            width={videoWidth}
            height={videoHeight}
            mirrored={false}
            enabled={true}
            videoElement={videoRef.current}
            previewTrajectory={travelPreviewPoints}
          />

          {/* Shared Landmark Picker Overlay */}
          {landmarkSelectionMode.type !== 'idle' && poseData && (
            <LandmarkPicker
              poseData={poseData}
              videoWidth={videoWidth}
              videoHeight={videoHeight}
              containerRef={videoContainerRef}
              mode={
                landmarkSelectionMode.type === 'distance-source' ? 'selecting-source' :
                landmarkSelectionMode.type === 'distance-target' ? 'selecting-target' :
                landmarkSelectionMode.type === 'movement-landmark' ? 'selecting-source' :
                landmarkSelectionMode.type === 'axis-start' ? 'selecting-source' :
                landmarkSelectionMode.type === 'axis-end' ? 'selecting-target' :
                landmarkSelectionMode.type === 'timing-landmark-a' ? 'selecting-source' :
                landmarkSelectionMode.type === 'timing-landmark-b' ? 'selecting-target' :
                landmarkSelectionMode.type === 'external-point' ? 'selecting-source' :
                'idle'
              }
              sourceLandmark={
                landmarkSelectionMode.type === 'distance-source' || landmarkSelectionMode.type === 'distance-target'
                  ? spatialGoal?.source_landmark
                  : landmarkSelectionMode.type === 'movement-landmark'
                  ? movementLandmark
                  : landmarkSelectionMode.type === 'axis-start' || landmarkSelectionMode.type === 'axis-end'
                  ? axisConfig?.landmark_start
                  : landmarkSelectionMode.type === 'timing-landmark-a' || landmarkSelectionMode.type === 'timing-landmark-b'
                  ? timingGoal?.landmark_a
                  : undefined
              }
              targetLandmark={
                landmarkSelectionMode.type === 'timing-landmark-b'
                  ? timingGoal?.landmark_b
                  : landmarkSelectionMode.type === 'axis-end'
                  ? axisConfig?.landmark_end
                  : spatialGoal?.target?.target_landmark
              }
              onSourceSelect={handleLandmarkSelected}
              onTargetSelect={handleLandmarkSelected}
              onCancel={handleCancelLandmarkSelection}
            />
          )}
        </div>

        {/* Playback Controls */}
        <VideoPlaybackControls
          isPlaying={isPlaying}
          onTogglePlay={handlePlayPause}
          currentFrame={currentFrame}
          onFrameChange={handleFrameChange}
          totalFrames={totalFrames}
          fps={fps}
          isVideoReady={isVideoReady}
          currentPhase={currentPhase}
        />

        {/* Phase Timeline */}
        {phases && phases.length > 0 && (
          <div className="mb-2" data-guide-section="studio-panel-phase-timeline">
            <label className="text-xs font-medium text-text-secondary mb-1 block">Phases</label>
            <PhaseTimeline
              phases={phases.map(p => ({
                phase_id: p.id,
                phase_type: 'custom' as const,
                label: p.name,
                start_frame: p.start_frame,
                end_frame: p.end_frame,
                importance: (p.importance || 'medium') as 'low' | 'medium' | 'high' | 'critical',
                description: p.description
              }))}
              totalFrames={totalFrames}
              fps={fps}
              currentFrame={currentFrame}
              height={32}
              showFrameNumbers={false}
              showTimeLabels={false}
              onPhaseClick={(phase) => {
                handleSeekToFrame(phase.start_frame);
                setFrameMode('phase');
              }}
            />
          </div>
        )}

        {/* Annotation Timeline */}
        <AnnotationTimeline
          totalFrames={totalFrames}
          fps={fps}
          currentFrame={currentFrame}
          annotations={annotations}
          onSeek={handleSeekToFrame}
        />

        {/* Annotations List */}
        {annotations.length > 0 && (
          <div className="border border-border-subtle rounded-lg p-4 bg-dark-card">
            <h3 className="text-sm font-semibold text-text-primary mb-3">
              Saved Annotations ({annotations.length})
            </h3>
            {/*
              One line, once the coach has told us which side leads, saying
              what that will do to the focus areas still waiting on it. Shown
              only while at least one is waiting — when nothing is waiting
              there is nothing to promise.
            */}
            {declaredLeadSide && annotations.some(isFocusAreaAwaitingSide) && (
              <p className="text-xs text-info mb-3" data-testid="focus-area-side-line">
                {focusAreaSideLine(declaredLeadSide)}
              </p>
            )}
            {/*
              Taller than the 16rem this list used to cap at: a card now
              carries a why and a how and a placement action, and at phone
              width that is three or four lines, so 16rem showed barely one.
            */}
            <div className="space-y-2 max-h-80 sm:max-h-96 overflow-y-auto">
              {annotations.map((ann) => (
                <div
                  key={ann.id}
                  data-testid={`annotation-item-${ann.id}`}
                  onClick={() => setSelectedAnnotationId(ann.id)}
                  className={cn(
                    // Stacks on a phone, sits in a row from `sm` up, so the
                    // buttons never push the text off the edge.
                    'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3',
                    'rounded-lg border bg-dark-elevated-1 hover:bg-dark-elevated-2 transition-colors',
                    selectedAnnotationId === ann.id ? 'border-accent' : 'border-border-subtle',
                  )}
                >
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div
                      className="w-4 h-4 mt-0.5 rounded-full border-2 border-white shadow flex-shrink-0"
                      style={{ backgroundColor: ann.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant={
                            ann.importance === 'critical' || ann.importance === 'high'
                              ? 'error'
                              : ann.importance === 'medium'
                              ? 'warning'
                              : 'success'
                          }
                        >
                          {ann.importance}
                        </Badge>
                        <span className="text-sm font-medium text-text-primary" data-field-id={FIELD_IDS.ANNOTATION_DESCRIPTION}>
                          {ann.description || 'Unnamed'}
                        </span>
                        {ann.spatial_goal?.enabled && (
                          <Badge variant="secondary" className="text-xs bg-violet-500/20 text-violet-300">
                            Distance
                          </Badge>
                        )}
                        {ann.movement_goal?.enabled && (
                          <Badge variant="secondary" className="text-xs bg-success/20 text-success">
                            Movement
                          </Badge>
                        )}
                        {ann.axis_goal && (
                          <Badge variant="secondary" className="text-xs bg-amber-500/20 text-amber-300">
                            Axis
                          </Badge>
                        )}
                        {ann.timing_goal && (
                          <Badge variant="secondary" className="text-xs bg-cyan-500/20 text-cyan-300">
                            Timing
                          </Badge>
                        )}
                        {ann.joint_angle_goal && (
                          <Badge variant="secondary" className="text-xs bg-amber-500/20 text-amber-300">
                            Joint Angle
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="text-xs text-text-secondary capitalize">{ann.type}</span>
                        {/*
                          A focus area whose body points were resolved to one
                          side COUNTS in the score, with the weighting the coach
                          chose — so the card says that in plain words instead
                          of leaving the coach to read a badge. `resolution` is
                          the single signal for it (see the `Annotation` type).
                        */}
                        {ann.resolution && (
                          <span className="text-xs text-success font-medium">
                            {COUNTING_IN_SCORES_BADGE}
                          </span>
                        )}
                        {/*
                          A region the COACH drew: its body points came from the
                          box, not from a declared side, so it keeps the badge it
                          has always had. Unchanged by PR-4.
                        */}
                        {ann.tracking && !ann.resolution && (
                          <span className="text-xs text-info font-medium">Tracked</span>
                        )}
                        {/*
                          Not resolved: on the template, counting for nothing.
                          Either we have not decided yet (no notice — the PR-3
                          sentence, whose promise still holds), or we decided and
                          failed closed, in which case the backend's own
                          what / why / how says what to do about it and is
                          rendered VERBATIM below.
                        */}
                        {ann.tracking === undefined && (
                          <span className="text-xs text-warning font-medium">
                            {ann.notice ? NOT_COUNTING_YET_SHORT : NOT_COUNTING_YET_PENDING}
                          </span>
                        )}
                        {/*
                          PR-5 — what this check does when the athlete is off,
                          in the coach's words. Only ever from the backend's own
                          verdict (`gate_summary`): a card that worked the
                          answer out from `is_gate` would be a second,
                          independent decision about the same thing, which is
                          the rule CLAUDE.md's gate checklist item 11 states
                          ("never read `is_gate` ... to independently decide
                          pass/fail/zero"). No verdict ⇒ no line.
                        */}
                        {ann.gate_summary && gateSummarySentence(ann.gate_summary) && (
                          <span
                            className={cn(
                              'text-xs font-medium',
                              ann.gate_summary.shape === 'none'
                                ? 'text-text-secondary'
                                : 'text-warning',
                            )}
                            data-testid={`gate-summary-${ann.id}`}
                          >
                            {gateSummarySentence(ann.gate_summary)}
                          </span>
                        )}
                        {ann.target_phase && (
                          <span className="text-xs text-info font-medium">
                            Phase: {ann.target_phase}
                          </span>
                        )}
                        {ann.activeFrames?.mode === 'range' && !ann.target_phase && (
                          <span className="text-xs text-accent font-medium">
                            Frames {ann.activeFrames.startFrame}–{ann.activeFrames.endFrame}
                          </span>
                        )}
                      </div>

                      {/*
                        Which body parts this check is watching, and — when it
                        was resolved from a declared side — which side it landed
                        on, in the coach's own words.

                        Replaces the "{N} landmarks" count this row used to
                        show, which both named an internal identifier and gave
                        the coach a number they could do nothing with
                        (docs/COACH_FACING_COPY_STANDARD.md §2). It renders for
                        a coach-DRAWN region too, so removing the count removes
                        no information: that one has body-point names from the
                        box it was drawn around, just no declared side.
                      */}
                      {regionWatchingSentence(ann.landmark_names, ann.resolution?.side) && (
                        <p className="text-xs text-text-secondary mt-1">
                          {regionWatchingSentence(ann.landmark_names, ann.resolution?.side)}
                        </p>
                      )}

                      {/*
                        The backend's what / why / how for a region it refused
                        to resolve, rendered VERBATIM — it owns every
                        coach-facing sentence in a notice (the materialise
                        contract's note 2). For `side_not_declared` its "how"
                        is what points the coach at the lead-side control.
                      */}
                      {ann.tracking === undefined && ann.notice && (
                        <div className="mt-1 space-y-0.5" data-testid={`region-notice-${ann.id}`}>
                          <p className="text-xs text-text-primary">{ann.notice.what}</p>
                          <p className="text-xs text-text-secondary">{ann.notice.why}</p>
                          <p className="text-xs text-text-secondary">{ann.notice.how}</p>
                        </div>
                      )}

                      {/*
                        Placement assistance (owner decision D5). Offered only
                        for a check with no box yet: once it has one, the
                        existing edit / drag path owns it.
                      */}
                      {!ann.coordinates && (() => {
                        const state = placementStates[ann.id] ?? { status: 'idle' };
                        const blockedBefore: PlacementBlockedReason | null = !ann.resolution
                          ? 'unresolved'
                          : !templateId
                          ? 'template_not_saved'
                          : null;
                        const reason =
                          state.status === 'blocked' ? state.reason : blockedBefore;
                        return (
                          <div className="mt-2">
                            <button
                              type="button"
                              data-testid={`place-on-video-${ann.id}`}
                              disabled={blockedBefore !== null || state.status === 'loading'}
                              onClick={(e) => {
                                e.stopPropagation();
                                void handlePlaceOnVideo(ann.id);
                              }}
                              className={cn(
                                // Full width on a phone so it is a comfortable
                                // tap target; shrink-to-fit from `sm` up.
                                'w-full sm:w-auto px-3 py-2 sm:py-1.5 text-xs font-medium rounded transition-colors',
                                blockedBefore !== null || state.status === 'loading'
                                  ? 'bg-dark-elevated-2 text-text-muted cursor-not-allowed'
                                  : 'bg-accent/15 text-accent hover:bg-accent/25',
                              )}
                            >
                              {state.status === 'loading'
                                ? PLACE_ON_VIDEO_BUSY_LABEL
                                : PLACE_ON_VIDEO_LABEL}
                            </button>
                            {reason && (
                              <p
                                className="text-xs text-text-muted mt-1"
                                data-testid={`place-blocked-${ann.id}`}
                              >
                                {PLACEMENT_BLOCKED_SENTENCES[reason]}
                              </p>
                            )}
                          </div>
                        );
                      })()}

                      {/*
                        Say that the box can be moved — before PR-4 a selected
                        annotation could only be replaced by a full redraw and
                        nothing announced otherwise (audit item FE-9).
                      */}
                      {selectedAnnotationId === ann.id &&
                        ann.coordinates &&
                        // While it is open for editing the canvas shows its
                        // pending copy instead, and the editing banner carries
                        // the same hint — saying it twice would be two
                        // wordings of one instruction on one screen (FE-12).
                        editingAnnotationId !== ann.id && (
                          <p className="text-xs text-accent mt-1" data-testid={`selected-hint-${ann.id}`}>
                            {SELECTED_ON_CANVAS_HINT}
                          </p>
                        )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 self-end sm:self-auto flex-shrink-0">
                    <button
                      onClick={() => handleEdit(ann.id)}
                      className="p-1.5 text-text-secondary hover:text-accent hover:bg-accent/10 rounded transition-colors"
                      title="Edit"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(ann.id)}
                      className="p-1.5 text-danger hover:bg-danger/10 rounded transition-colors"
                      title="Delete"
                      data-testid={`delete-annotation-${ann.id}`}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Drawing Instructions */}
        {drawingMode && (
          <Alert variant="info">
            {drawingMode === 'rectangle' && 'Click and drag to draw a rectangle'}
            {drawingMode === 'circle' && 'Click center, then drag to set radius'}
          </Alert>
        )}
      </div>
    </div>
  );
});

RegionAnnotator.displayName = 'RegionAnnotator';

export default RegionAnnotator;
